import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Download, X } from 'lucide-react';
import { ATELIER, FONTS } from '../tokens';
import { useWorkspace } from '../WorkspaceContext';
import { ThreadHeader } from './ThreadHeader';
import { Thread } from './Thread';
import { SmartComposer } from './SmartComposer';
import { BentoShell } from './bento/BentoShell';
import { FloatingComposer } from './bento/FloatingComposer';
import { SpreadsheetPanel } from '../panels/SpreadsheetPanel';
import PresentationPanel from '../panels/PresentationPanel';

/**
 * ThreadView - the main column.
 *
 * Two layouts:
 *
 *   1. **First run / no active project / no turns** →
 *        The premium Bento dashboard shell. The composer lives INSIDE the
 *        hero bento tile. No separate header, no footer composer. The
 *        workspace feels like a dashboard when it's empty.
 *
 *   2. **Project open (with or without turns)** →
 *        Classic thread: ThreadHeader / Thread (or ThreadAtrium) / composer
 *        pinned at the bottom. The thread is the work, so linearity wins.
 */
export function ThreadView() {
  const { activeProjectId, activeMode, activePreview, expandedMedia, setExpandedMedia } = useWorkspace();
  const inProject = Boolean(activeProjectId);

  // Bento is the home/overview. Linear thread takes over once a project is open or slides studio is active.
  // Use the Home button in the sidebar (goHome) to return here.
  const showBento = !inProject && activeMode !== 'slides';

  // Responsive state tracking for mobile viewport
  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 1024);
  const [mobileTab, setMobileTab] = React.useState('chat'); // 'chat' | 'preview'

  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Proactively shift view to preview when a preview is newly generated or available on mobile
  React.useEffect(() => {
    if (activePreview && isMobile) {
      setMobileTab('preview');
    } else {
      setMobileTab('chat');
    }
  }, [activePreview, isMobile]);

  return (
    <>
      {showBento ? (
        <main style={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          background: ATELIER.bgSubtle,
          fontFamily: FONTS.sans,
          height: '100%',
          overflow: 'hidden',
          position: 'relative',
        }}>
          {isMobile ? <MinimalMobileHome /> : <BentoShell />}
          <FloatingComposer />
        </main>
      ) : (
        <main style={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          background: ATELIER.bg,
          position: 'relative',
          fontFamily: FONTS.sans,
          height: '100%',
          overflow: 'hidden',
        }}>
          {/* LEFT CHAT THREAD COLUMN */}
          {(!isMobile || mobileTab === 'chat') && (
            <div style={{
              flex: (!isMobile && activePreview) ? '0 0 45%' : 1,
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              transition: 'all 0.35s cubic-bezier(0.16, 1, 0.3, 1)',
              position: 'relative',
            }}>
              <ThreadHeader />
              
              {/* Premium Segmented Toggle Tabs on Mobile View when Preview is Active */}
              {isMobile && activePreview && (
                <div style={{
                  padding: '8px 16px',
                  background: ATELIER.bg,
                  borderBottom: `1px solid ${ATELIER.borderSoft}`,
                  display: 'flex',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  <div style={{
                    display: 'flex',
                    background: ATELIER.bgHover,
                    borderRadius: 12,
                    padding: 3,
                    width: '100%',
                    maxWidth: 400,
                    border: `1px solid ${ATELIER.border}`,
                  }}>
                    <button
                      onClick={() => setMobileTab('chat')}
                      style={{
                        flex: 1,
                        padding: '8px 12px',
                        borderRadius: 9,
                        border: 'none',
                        fontFamily: FONTS.sans,
                        fontSize: 13,
                        fontWeight: 600,
                        cursor: 'pointer',
                        background: mobileTab === 'chat' ? '#fff' : 'transparent',
                        color: mobileTab === 'chat' ? ATELIER.accent : ATELIER.inkMid,
                        boxShadow: mobileTab === 'chat' ? '0 2px 8px rgba(0,0,0,0.06)' : 'none',
                        transition: 'all 0.2s cubic-bezier(0.16, 1, 0.3, 1)',
                      }}
                    >
                      Chat Thread
                    </button>
                    <button
                      onClick={() => setMobileTab('preview')}
                      style={{
                        flex: 1,
                        padding: '8px 12px',
                        borderRadius: 9,
                        border: 'none',
                        fontFamily: FONTS.sans,
                        fontSize: 13,
                        fontWeight: 600,
                        cursor: 'pointer',
                        background: mobileTab === 'preview' ? '#fff' : 'transparent',
                        color: mobileTab === 'preview' ? ATELIER.accent : ATELIER.inkMid,
                        boxShadow: mobileTab === 'preview' ? '0 2px 8px rgba(0,0,0,0.06)' : 'none',
                        transition: 'all 0.2s cubic-bezier(0.16, 1, 0.3, 1)',
                      }}
                    >
                      Workspace Preview
                    </button>
                  </div>
                </div>
              )}
              
              <Thread />
              <SmartComposer />
            </div>
          )}

          {/* RIGHT PREVIEW WORKSPACE COLUMN */}
          {(!isMobile || mobileTab === 'preview') && activePreview && (
            <div style={{
              flex: isMobile ? 1 : '0 0 55%',
              minWidth: 0,
              height: '100%',
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              background: ATELIER.bg,
            }}>
              {/* Immersive Mobile Header for full-screen Preview Mode */}
              {isMobile && (
                <div style={{
                  height: 60,
                  borderBottom: `1px solid ${ATELIER.border}`,
                  background: ATELIER.bg,
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0 16px',
                  flexShrink: 0,
                }}>
                  <button
                    onClick={() => setMobileTab('chat')}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      background: 'transparent',
                      border: 'none',
                      color: ATELIER.accent,
                      fontFamily: FONTS.sans,
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="19" y1="12" x2="5" y2="12"></line>
                      <polyline points="12 19 5 12 12 5"></polyline>
                    </svg>
                    Back to Chat
                  </button>
                  <span style={{ flex: 1 }} />
                  <span style={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: ATELIER.inkSoft,
                    textTransform: 'capitalize',
                  }}>
                    {activePreview.type} Preview
                  </span>
                </div>
              )}
              
              <div style={{ flex: 1, minWidth: 0, position: 'relative' }}>
                <AnimatePresence mode="wait">
                  {activePreview.type === 'spreadsheet' && (
                    <div style={{ width: '100%', height: '100%', position: 'relative' }}>
                      <SpreadsheetPanel />
                    </div>
                  )}
                  {activePreview.type === 'presentation' && (
                    <div style={{ width: '100%', height: '100%', position: 'relative' }}>
                      <PresentationPanel />
                    </div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          )}
        </main>
      )}

      {/* Global Media Lightbox */}
      <AnimatePresence>
        {expandedMedia && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setExpandedMedia(null)}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(255, 255, 255, 0.98)',
              backdropFilter: 'blur(20px)',
              zIndex: 10000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '40px',
              cursor: 'zoom-out',
            }}
          >
            <motion.div
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              style={{
                maxWidth: '90vw',
                maxHeight: '85vh',
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 20,
              }}
            >
              {expandedMedia.type === 'image' ? (
                <img
                  src={expandedMedia.url}
                  alt={expandedMedia.title || 'Expanded view'}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '75vh',
                    objectFit: 'contain',
                    borderRadius: 12,
                    boxShadow: '0 32px 64px rgba(0,0,0,0.12)',
                    border: `1px solid ${ATELIER.border}`,
                  }}
                />
              ) : (
                <video
                  src={expandedMedia.url}
                  controls
                  autoPlay
                  playsInline
                  style={{
                    maxWidth: '100%',
                    maxHeight: '75vh',
                    borderRadius: 12,
                    boxShadow: '0 32px 64px rgba(0,0,0,0.12)',
                    border: `1px solid ${ATELIER.border}`,
                    background: '#000',
                  }}
                />
              )}

              {/* Toolbar in Modal */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
              }}>
                <button
                  onClick={() => {
                    const apiUrl = 'https://api.daggpt.network';
                    const proxyUrl = `${apiUrl}/api/download?url=${encodeURIComponent(expandedMedia.url)}`;
                    const a = document.createElement('a');
                    a.href = proxyUrl;
                    a.download = `dag-export-${Date.now()}.jpg`;
                    a.click();
                  }}
                  style={{
                    padding: '10px 20px',
                    borderRadius: 12,
                    background: ATELIER.accent,
                    color: '#fff',
                    border: 'none',
                    fontFamily: FONTS.sans,
                    fontSize: 13,
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    cursor: 'pointer',
                    boxShadow: '0 4px 12px rgba(124,58,237,0.3)',
                  }}
                >
                  <Download size={16} />
                  Download
                </button>
                <button
                  onClick={() => setExpandedMedia(null)}
                  style={{
                    width: 42, height: 42, borderRadius: 12,
                    background: '#fff',
                    border: `1px solid ${ATELIER.border}`,
                    color: ATELIER.ink,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer',
                  }}
                >
                  <X size={20} />
                </button>
              </div>

              {expandedMedia.title && (
                <div style={{
                  maxWidth: 600,
                  textAlign: 'center',
                  fontSize: 14,
                  color: ATELIER.inkSoft,
                  lineHeight: 1.5,
                  background: ATELIER.bgHover,
                  padding: '12px 20px',
                  borderRadius: 12,
                  border: `1px solid ${ATELIER.borderSoft}`,
                }}>
                  {expandedMedia.title}
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

function MinimalMobileHome() {
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      flex: 1,
      padding: '40px 24px',
      textAlign: 'center',
      gap: 16,
      background: ATELIER.bgSubtle,
    }}>

      <h1 style={{
        fontSize: 26,
        fontWeight: 800,
        fontFamily: FONTS.sans,
        color: ATELIER.ink,
        letterSpacing: '-0.02em',
        margin: 0,
      }}>
        What shall we <span style={{ color: ATELIER.accent }}>make</span> today?
      </h1>

      <p style={{
        fontSize: 14,
        color: ATELIER.inkSoft,
        maxWidth: 290,
        lineHeight: 1.55,
        margin: 0,
        fontFamily: FONTS.sans,
      }}>
        One thread. Infinite capabilities. Ask questions, brainstorm creative ideas, generate gorgeous images, videos, or original music.
      </p>
    </div>
  );
}
